/**
 * buildEventsExportRows — the Events list export's mapRows step (moved out of Events.tsx
 * 2026-10-26 so it can be tested).
 *
 * Hydrates, in parallel:
 *  - close-rate stats (chunks of 5000 event ids; the RPC's own cap),
 *  - sales-position names (chunks of 5000 position ids),
 *  - promo codes (chunks of 500 event ids; get_event_promo_codes returns nothing above 500).
 *
 * Every lookup is STRICT: any failure rejects, and ExportButton shows the error toast instead
 * of downloading a file with silently blank columns.
 */
import {
  getEventCloseRateStatsBulk,
  resolvePositionNamesBulk,
  indexCloseRateStatsByEventId,
  indexPositionsById,
} from "../../services/reportsSuite.service";
import { getEventPromoCodesStrict } from "../../services/events.service";
import { eventRowToExport, type EventLike } from "./eventsExport";
import { getEventDayHours } from "../../services/eventDayHours.service";
import { dailyHoursApplicability } from "../../utils/eventDayHours";

const chunk = <T>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
};

export async function buildEventsExportRows(rows: EventLike[]) {
  const eventIds: string[] = rows.map((r) => r.id).filter(Boolean);
  const positionIds = Array.from(
    new Set(
      rows.flatMap((r) =>
        [
          r.sales_position_id,
          r.territory_manager_position_id,
          r.regional_manager_position_id,
          r.sales_director_position_id,
        ].filter((id): id is string => Boolean(id)),
      ),
    ),
  );

  // Hours each day only for events that can have them (multi-day, timed); strict like the other lookups.
  const dayHoursIds = rows
    .filter((r) => r.id && dailyHoursApplicability(r).ok)
    .map((r) => r.id);

  const [statsChunks, positionChunks, promoCodesByEventId, dayHoursByEventId] =
    await Promise.all([
      Promise.all(
        chunk(eventIds, 5000).map((c) => getEventCloseRateStatsBulk(c)),
      ),
      Promise.all(
        chunk(positionIds, 5000).map((c) => resolvePositionNamesBulk(c)),
      ),
      // Chunks itself (<= 500 ids per call) and throws on any failed chunk.
      getEventPromoCodesStrict(eventIds),
      dayHoursIds.length > 0
        ? getEventDayHours(dayHoursIds)
        : Promise.resolve(new Map()),
    ]);

  const statsByEventId = indexCloseRateStatsByEventId(statsChunks.flat());
  const positionsById = indexPositionsById(positionChunks.flat());
  return rows.map((r) =>
    eventRowToExport(r, {
      statsByEventId,
      positionsById,
      promoCodesByEventId,
      dayHoursByEventId,
    }),
  );
}
